import { createRouter, createWebHistory } from 'vue-router';
import store from './store';
import VueCookies from 'vue-cookies';
import { applyMarketingLang } from './composables/useMarketingLang';
import { MARKETING_PUBLIC_PATHS, isPublicAccessPath, hidesVerticalBars, normalizeMarketingPath } from './utils/marketingPages';

const Signin = () => import(/* webpackChunkName: "auth-signin" */ './views/Signin.vue');
const Signup = () => import(/* webpackChunkName: "auth-signup" */ './views/Signup.vue');
const Search = () => import(/* webpackChunkName: "search" */ './views/Search.vue');

const ManageTemplates = () => import(/* webpackChunkName: "templates" */ './views/ManageTemplates.vue');
const ManageMembershipCard = () => import(/* webpackChunkName: "templates" */ './views/ManageMembershipCard.vue');
const ManageSubCommunities = () => import(/* webpackChunkName: "community" */ './views/ManageSubCommunities.vue');
const ManageMembers = () => import(/* webpackChunkName: "community" */ './views/ManageMembers.vue');
const ManageChannel = () => import(/* webpackChunkName: "community" */ './views/ManageChannel.vue');
const ManageQuests = () => import(/* webpackChunkName: "community" */ './views/ManageQuests.vue');

const SettingsManageProfile = () => import(/* webpackChunkName: "settings" */ './views/Settings/ManageProfile.vue');
const SettingsManageLanguage = () => import(/* webpackChunkName: "settings" */ './views/Settings/ManageLanguage.vue');
const SettingsManageSubscription = () => import(/* webpackChunkName: "settings-subscription" */ './views/Settings/ManageSubscription.vue');
const SettingsManagePromote = () => import(/* webpackChunkName: "settings" */ './views/Settings/Promote.vue');
const SettingsManageLoginMethod = () => import(/* webpackChunkName: "settings" */ './views/Settings/ManageLoginMethod.vue');
const SettingsTermsOfService = () => import(/* webpackChunkName: "legal" */ './views/Settings/TermsOfService.vue');
const SettingsPrivacyPolicy = () => import(/* webpackChunkName: "legal" */ './views/Settings/PrivacyPolicy.vue');
const SettingsLegalNotices = () => import(/* webpackChunkName: "legal" */ './views/Settings/LegalNotices.vue');
const SettingsCookieManagementPolicy = () => import(/* webpackChunkName: "legal" */ './views/Settings/CookieManagementPolicy.vue');
const SettingsManageAccount = () => import(/* webpackChunkName: "settings" */ './views/Settings/ManageAccount.vue');
const SettingsApiKey = () => import(/* webpackChunkName: "settings" */ './views/Settings/ManageApiKey.vue');
const SettingsAdvanced = () => import(/* webpackChunkName: "settings" */ './views/Settings/ManageAdvanced.vue');
const Protected = () => import(/* webpackChunkName: "auth-static" */ './views/Protected.vue');
const Expired = () => import(/* webpackChunkName: "auth-static" */ './views/Expired.vue');
const MultiAuth = () => import(/* webpackChunkName: "auth-magic" */ './views/MultiAuth.vue');
const BrandAnalyzer = () => import(/* webpackChunkName: "brand-analyzer" */ './views/BrandAnalyzer.vue');
const CommunityCreate = () => import(/* webpackChunkName: "community-create" */ './views/CommunityCreate.vue');
const Landing = () => import(/* webpackChunkName: "landing" */ './views/Landing.vue');
const Blog = () => import(/* webpackChunkName: "blog" */ './views/Blog.vue');
const BlogPost = () => import(/* webpackChunkName: "blog" */ './views/BlogPost.vue');
const Press = () => import(/* webpackChunkName: "press" */ './views/Press.vue');

const redirectLegacyMarketingPageToHome = (to) => ({
  path: '/',
  query: to.query,
});

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', component: Landing },
    { path: '/signin', component: Signin },
    { path: '/signup', component: Signup },
    { path: '/search', component: Search },
    { path: '/brand-analyzer', name: 'brand-analyzer', component: BrandAnalyzer },
    { path: '/community-create', component: CommunityCreate },
    { path: '/templates/basic', component: ManageTemplates },
    {
      path: '/templates/participation',
      component: ManageTemplates,
    },
    {
      path: '/templates/ticket',
      component: ManageTemplates,
    },
    { path: '/templates/membership', component: ManageMembershipCard },
    { path: '/templates/role', component: ManageTemplates },
    { path: '/members', component: ManageMembers },
    { path: '/channel', component: ManageChannel },
    { path: '/quests', component: ManageQuests },
    { path: '/sub-communities', component: ManageSubCommunities },
    { path: '/Settings/profile', component: SettingsManageProfile },
    { path: '/Settings/language', component: SettingsManageLanguage },
    { path: '/Settings/subscription', component: SettingsManageSubscription },
    { path: '/Settings/plans', component: SettingsManageSubscription },
    { path: '/Settings/promote', component: SettingsManagePromote },
    { path: '/Settings/terms', component: SettingsTermsOfService },
    { path: '/Settings/privacy', component: SettingsPrivacyPolicy },
    { path: '/Settings/legal', component: SettingsLegalNotices },
    { path: '/Settings/cookies', component: SettingsCookieManagementPolicy },
    { path: '/Settings/account', component: SettingsManageAccount },
    { path: '/Settings/api-key', component: SettingsApiKey },
    { path: '/Settings/advanced', component: SettingsAdvanced },
    { path: '/Settings/login-method', component: SettingsManageLoginMethod },
    { path: '/protected', component: Protected },
    { path: '/expired', component: Expired },
    { path: '/auth', component: MultiAuth },
    { path: '/lead1', redirect: redirectLegacyMarketingPageToHome },
    { path: '/lead2', redirect: redirectLegacyMarketingPageToHome },
    { path: '/partner', redirect: redirectLegacyMarketingPageToHome },
    { path: '/blog', component: Blog },
    { path: '/blog/:slug', component: BlogPost },
    { path: '/presse', component: Press },
    { path: '/press', redirect: '/presse' },
    { path: '/:notFound(.*)', redirect: '/' },
  ],
});

const ROUTER_INIT_SESSION_TIMEOUT_MS = (() => {
  const n = Number(process.env.VUE_APP_ROUTER_SESSION_TIMEOUT_MS || 12000);
  if (!Number.isFinite(n) || n <= 0) return 12000;
  return Math.max(4000, Math.min(Math.round(n), 45000));
})();

function isBootDebugEnabled() {
  try {
    const qp = new URLSearchParams(window.location.search).get('debugBoot');
    if (qp === '1' || qp === 'true') return true;
    return localStorage.getItem('debug_boot') === '1';
  } catch (_err) {
    return false;
  }
}

async function withTimeout(promise, timeoutMs, timeoutMessage) {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

router.beforeEach(async (to, from, next) => {
  const debugBoot = isBootDebugEnabled();
  const guardStartTs = Date.now();
  try {
    const destinationPath = normalizeMarketingPath(to.path);
    const isHomePath = destinationPath === '/';
    // Pages publiques SANS tentative de restauration de session.
    // Pour /signin, /signup et /auth, on tente volontairement /auth/me afin que
    // le bouton "Back" ne laisse pas l'utilisateur sur une page d'auth si la session est encore valide.
    // Exception : / vérifie aussi la session pour rediriger les utilisateurs connectés vers le dashboard.
    const noAuthNeededPages = ['/protected', '/expired', ...MARKETING_PUBLIC_PATHS];
    const needsAuthCheck = !noAuthNeededPages.includes(to.path) || isHomePath;

    // ── Récupérer la session via Vuex (cache /auth/me) ───────────────────────────
    // Si la session est déjà dans le store et non expirée, on l'utilise directement
    // (évite un appel /auth/me redondant juste après finalizeMagicAuth)
    const cachedSession = store.getters.getSessionInfo;
    const nowSec = Date.now() / 1000;
    const sessionStillValid =
      cachedSession && (!cachedSession.exp || cachedSession.exp > nowSec);
    const session = needsAuthCheck
      ? (sessionStillValid
        ? cachedSession
        : await withTimeout(
          store.dispatch('initSession'),
          ROUTER_INIT_SESSION_TIMEOUT_MS,
          `BOOT_ROUTER_INIT_SESSION_TIMEOUT_${ROUTER_INIT_SESSION_TIMEOUT_MS}MS`
        ))
      : cachedSession;
    const hasValidAuth = !!session && !session.isTmp;
    const hasAnyAuth = !!session; // inclut token_tmp (signup en cours)

    // ── Langue ───────────────────────────────────────────────────────────────────
    const isPublicPage = isPublicAccessPath(to.path);

    if (isPublicPage) {
      const queryLang = typeof to.query.lang === 'string' ? to.query.lang : '';
      if (queryLang === 'fr' || queryLang === 'en') {
        applyMarketingLang(queryLang);
      } else {
        const savedLocale = localStorage.getItem('user-locale') || localStorage.getItem('locale');
        if (!savedLocale) {
          const browserLang = (navigator.language || navigator.userLanguage || 'en').split('-')[0];
          applyMarketingLang(['fr', 'en'].includes(browserLang) ? browserLang : 'en');
        } else if (['fr', 'en'].includes(savedLocale)) {
          applyMarketingLang(savedLocale, { persist: false });
        }
      }
    } else if (hasAnyAuth) {
      const savedLocale = localStorage.getItem('locale') || localStorage.getItem('user-locale');
      if (savedLocale && ['fr', 'en'].includes(savedLocale)) {
        applyMarketingLang(savedLocale, { persist: false });
      } else {
        const browserLang = (navigator.language || navigator.userLanguage || 'en').split('-')[0];
        applyMarketingLang(['fr', 'en'].includes(browserLang) ? browserLang : 'en');
      }
    }

    // ── Protection par mot de passe ──────────────────────────────────────────────
    if (process.env.VUE_APP_PASSWORD && process.env.VUE_APP_PASSWORD.length > 0) {
      const validCodes = process.env.VUE_APP_PASSWORD.split(',').map((c) => c.trim());
      const userCode = VueCookies.get('pwd');
      const passwordValid = userCode && validCodes.includes(userCode.trim());

      if (passwordValid && to.path == '/protected') return next('/');
      if (!passwordValid) {
        VueCookies.remove('pwd');
        if (to.path != '/protected') return next('/protected');
      }
    }

    store.commit('setVerticalBarsDisplay', !hidesVerticalBars(to.path));

    // ── Contrôle d'accès ─────────────────────────────────────────────────────────
    if (!hasValidAuth) {
      // token_tmp uniquement autorisé sur /signup et /auth
      if (hasAnyAuth && ['/signup', '/auth'].includes(to.path)) {
        return next();
      }
      // Pas de session : rediriger vers /signin sauf pages publiques
      if (!isPublicPage) {
        if (process.env.VUE_APP_PASSWORD && process.env.VUE_APP_PASSWORD.length > 0) {
          if (['/signup', '/signin', '/expired', '/protected', '/auth'].indexOf(to.path) == -1) {
            return next('/signin');
          }
        } else {
          if (['/signup', '/signin', '/expired', '/auth'].indexOf(to.path) == -1) {
            return next('/signin');
          }
        }
      }
    } else {
      // Session valide : accueil marketing → dashboard
      if (isHomePath) {
        return next('/brand-analyzer');
      }
      // Session valide : rediriger hors des pages auth vers brand-analyzer (pas badges)
      // Exception stricte : autoriser /signup uniquement pour "switch account -> create new".
      const isSignupFromSwitchCreateNew =
        to.path === '/signup' &&
        to.query?.connected === '1' &&
        to.query?.flow === 'switch-create-new';
      if (isSignupFromSwitchCreateNew) {
        return next();
      }
      const authPages = ['/signup', '/signin', '/expired', '/auth'];
      if (process.env.VUE_APP_PASSWORD && process.env.VUE_APP_PASSWORD.length > 0) {
        if (authPages.includes(to.path) || to.path === '/protected') return next('/brand-analyzer');
      } else if (authPages.includes(to.path)) {
        return next('/brand-analyzer');
      }
    }

    // ── /community-create : autoriser prekey (Web2) OU validated + Web3 (Nostr, pas de switch-prekey) ──
    if (to.path === '/community-create' && hasAnyAuth) {
      const currentIssuerInfo = store.getters.getCurrentIssuerInfo;
      const isWeb3 = store.getters.isWeb3Connexion;
      const allowedPrekey = currentIssuerInfo?.state === 'prekey';
      const allowedWeb3Validated =
        currentIssuerInfo?.state === 'validated' && isWeb3;
      if (currentIssuerInfo && !allowedPrekey && !allowedWeb3Validated) {
        return next('/');
      }
    }

    if (debugBoot) {
      console.info('[BOOT][router] beforeEach:ok', {
        to: to.path,
        from: from?.path || null,
        durationMs: Date.now() - guardStartTs,
      });
    }
    return next();
  } catch (error) {
    console.error('[BOOT][router] beforeEach:error', {
      to: to?.path || null,
      from: from?.path || null,
      message: error?.message || 'unknown_error',
      durationMs: Date.now() - guardStartTs,
    });

    if (isPublicAccessPath(to.path)) return next();
    return next('/signin');
  }
});

export default router;
